import { Injectable } from "@angular/core";
import { FormattedValue } from "../models/value-formatting";
import { EMPTY_BYTES, EMPTY_VAL, SI_SIZES, SI_UNIT_MAP } from "../constant/value-formatting";
import Decimal from "decimal.js";
import { TableId } from "../models/vcloud-api";
import { REGEX_CAMEL_CASE } from "../constant/validation";
import { LoggerService } from "./logger.service";
import { ATTR_REGEX } from "../constant/vcloud-api";

@Injectable({
  providedIn: "root",
})
export class FormattingService {
  private prettifyCache: { [k: string]: string } = {};

  constructor(private log: LoggerService) {}

  public parseAttribute(attr: string): { table?: TableId; attribute: string } {
    const groups = ATTR_REGEX.exec(attr)?.groups;
    return {
      attribute: groups?.rest ?? attr,
      table: (groups?.table ?? undefined) as TableId | undefined,
    };
  }

  public decodeGCPEmail(email: string): string {
    if (!email) {
      return "--";
    }
    let letters = "";
    let atFound = false;
    for (let i = email.length - 1; i >= 0; i--) {
      if (!atFound && email[i] === "-") {
        letters += "@";
        atFound = true;
      } else if (email[i] === "_") {
        letters += ".";
      } else {
        letters += email[i];
      }
    }
    return letters.split("").reverse().join("");
  }

  public prettifyAttribute(attribute: string): string {
    if (!attribute) {
      return "";
    }

    if (attribute in this.prettifyCache) {
      return this.prettifyCache[attribute];
    }

    let attributeNormalised: string = attribute;

    if (attributeNormalised.includes("line_item_unblended_cost")) {
      attributeNormalised = "Actual Cost";
    } else if (attributeNormalised.startsWith("line_item_")) {
      attributeNormalised = attributeNormalised.slice("line_item_".length, attributeNormalised.length);
    } else if (attributeNormalised.startsWith("billing_usage_")) {
      attributeNormalised = attributeNormalised.slice("billing_usage_".length, attributeNormalised.length);
    }

    let attributeFormatted: string = this.parseAttribute(attributeNormalised)
      .attribute.split("_")
      .map((w) => w.slice(0, 1).toUpperCase() + w.slice(1))
      .filter(Boolean)
      .join(" ")
      .replace(REGEX_CAMEL_CASE, "$1 $2");

    if (attributeFormatted.endsWith(" Name") || attributeFormatted.endsWith(" name")) {
      attributeFormatted = attributeFormatted.slice(0, attributeFormatted.length - " name".length);
    }

    if (attributeFormatted.endsWith(" Id") || attributeFormatted.endsWith(" id")) {
      attributeFormatted = `${attributeFormatted.slice(0, attributeFormatted.length - " id".length)} ID`;
    }

    if (attributeFormatted.toLowerCase() === "bu") {
      return "Business Unit";
    }

    this.prettifyCache[attribute] = attributeFormatted;

    this.log.info({ msg: "finished formatting", oldValue: attribute, newValue: attributeFormatted });

    return attributeFormatted;
  }

  public fmtDay<T extends number | string | Date>(dateString: T): string {
    const tp = typeof dateString as "number" | "object" | "string";
    try {
      const date = tp === "object" ? (dateString as Date) : new Date(dateString);
      return `${date.toLocaleString(navigator.language || "en-GB", { day: "numeric" })} ${date.toLocaleString(
        navigator.language || "en-GB",
        { month: "short" },
      )}`;
    } catch (e) {
      if (tp === "string") {
        return dateString as string;
      } else {
        return (dateString ?? "").toString();
      }
    }
  }

  public fmtMonth<T extends number | string | Date>(dateString: T, format: "short" | "long" = "short"): string {
    const tp = typeof dateString as "number" | "object" | "string";
    try {
      const date = tp === "object" ? (dateString as Date) : new Date(dateString);
      return `${date.toLocaleString(navigator.language || "en-GB", { month: format })} ${date.getFullYear()}`;
    } catch (e) {
      if (tp === "string") {
        return dateString as string;
      } else {
        return (dateString ?? "").toString();
      }
    }
  }
  public fmtWeek<T extends number | string | Date>(dateString: T): string {
    const tp = typeof dateString as "number" | "object" | "string";
    try {
      const input = tp === "object" ? (dateString as Date) : new Date(dateString);

      const startDate = new Date(input);
      const day = startDate.getDay();
      const diffToMonday = day === 0 ? -6 : 1 - day;
      startDate.setDate(startDate.getDate() + diffToMonday);

      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);

      const locale = navigator.language || "en-GB";

      const startDay = startDate.toLocaleString(locale, { day: "numeric" });
      const startMonth = startDate.toLocaleString(locale, { month: "short" });
      const endDay = endDate.toLocaleString(locale, { day: "numeric" });
      const endMonth = endDate.toLocaleString(locale, { month: "short" });
      const year = startDate.getFullYear();

      return startMonth === endMonth
        ? `${startDay}–${endDay} ${startMonth} ${year}`
        : `${startDay} ${startMonth} – ${endDay} ${endMonth} ${year}`;
    } catch {
      return (dateString ?? "").toString();
    }
  }

  public fmtYear<T extends number | string | Date>(dateString: T): string {
    const tp = typeof dateString as "number" | "object" | "string";
    try {
      const date = tp === "object" ? (dateString as Date) : new Date(dateString);
      return date.getFullYear().toString();
    } catch (e) {
      if (tp === "string") {
        return dateString as string;
      } else {
        return (dateString ?? "").toString();
      }
    }
  }

  public formatNumber(value: number | null | undefined, decimalPlaces = 2): FormattedValue {
    if (value === 0 || value === null || value === undefined) {
      return EMPTY_VAL;
    } else {
      const absValue = Math.abs(value);
      const found = SI_UNIT_MAP.find(({ threshold }) => absValue >= threshold);
      if (found) {
        const decimal = new Decimal(value).dividedBy(found.threshold).toDecimalPlaces(decimalPlaces);
        const n = decimal.toNumber();
        const suffix = found.suffix;
        return {
          fmt: `${n}${suffix}`,
          unit: suffix,
          decimal,
          value: n,
        };
      } else {
        return {
          fmt: value.toString(),
          unit: "",
          decimal: new Decimal(value),
          value: value,
        };
      }
    }
  }

  public formatBytes(bytes: number | null | undefined, decimals = 2): FormattedValue {
    if (bytes === undefined || bytes === null || bytes === 0) {
      return EMPTY_BYTES;
    } else {
      const base = new Decimal(Math.log(bytes)).dividedBy(Math.log(1024)).floor().toNumber();
      const power = new Decimal(1024).pow(base);
      const decimal = new Decimal(bytes).dividedBy(power).toDecimalPlaces(decimals);
      const n = decimal.toNumber();
      return {
        fmt: `${n}${SI_SIZES[base]}`,
        decimal,
        unit: SI_SIZES[base],
        value: n,
      };
    }
  }

  public formatBytesSI(bytes: number | null | undefined): FormattedValue {
    if (bytes === undefined || bytes === null || bytes === 0) {
      return EMPTY_BYTES;
    } else {
      const base = 10;
      for (let idx = SI_SIZES.length - 1; idx >= 0; idx--) {
        const value = bytes / base ** (idx * 3);
        if (value >= 1 || idx === 0) {
          const unit = [...SI_SIZES].find((_size, i) => i === idx).replace("i", "");
          return {
            fmt: `${new Decimal(value).toDecimalPlaces(2)}${unit}`,
            decimal: new Decimal(value).toDecimalPlaces(2),
            unit: unit,
            value,
          };
        }
      }
    }
  }
}
